import React, { useEffect, useMemo } from 'react';

import { ApolloProvider } from '@apollo/client';

import { IBaseProps } from '@rbi-ctg/frontend';
import { useCdpContext } from 'state/cdp';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag, useGatewayFlags } from 'state/launchdarkly';
import { useLogger } from 'state/logger';

import { getClient } from './client';
import { withI18nContext } from './links/with-i18n-context';
import { withLogger } from './links/with-logger';
import { withMParticle } from './links/with-mparticle';
import { withSessionId } from './links/with-session-id';

export const GraphQLProvider = ({ children }: IBaseProps) => {
  const { language, region } = useLocale();
  const enableUserBlocking = useFlag(LaunchDarklyFlag.ENABLE_USER_BLOCKING);
  const gatewayFlags = useGatewayFlags();
  const client = useMemo(
    () => getClient(language, region, enableUserBlocking),
    [gatewayFlags, enableUserBlocking]
  ); // eslint-disable-line react-hooks/exhaustive-deps
  const logger = useLogger();
  const mparticle = useCdpContext();

  // update stateful links
  useEffect(() => {
    withI18nContext.setLocale(language, region);
  }, [client, language, region]);

  useEffect(() => {
    withSessionId.setSessionId(mparticle.sessionId);
  }, [mparticle.sessionId]);

  useEffect(() => {
    withMParticle.setMParticle(mparticle);
  }, [mparticle]);

  useEffect(() => {
    withLogger.setLogger(logger);
  }, [logger]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export { useLazySanityQuery, useSanityQuery } from './hooks/use-sanity-query';
